import {useEffect, useState} from "react";
import {Box, Stack, ToggleButtonGroup, Typography} from "@mui/material";
import {Navigate, useNavigate, useParams} from "react-router";
import {appSession} from "../../../TR3AD.ts";
import WidgetHeader from "../../../widgets/WidgetHeader.tsx";
import {appStyles} from "../../../AppStyles.ts";
import {StyledFilterToggleButton, StyledInputBase, StyledSearch, StyledSearchIconWrapper} from "../../AppStyledComponents.tsx";
import IntelProfileCard from "../../../cards/IntelProfileCard.tsx";
import type {SessionIntelProfile} from "../../../sdo/SessionIntelProfile.ts";

type FilterToggle =
    | "all"
    | "asset"
    | "attribute"
    | "category"
    | "contract"
    | "entity"
    | "event"
    | "factor"
    | "identifier"
    | "measurement"
    | "place"
    | "relationship"
    | "selector"
    | "temporal";

type IntelProfileSample = SessionIntelProfile & { type: Exclude<FilterToggle, "all"> };

// TODO: Replace with API call — mirrors desktop ODIExtensions.getIntelligenceDefinitions().getAll(),
// which returns the FULL catalog of registered intel definitions (not just populated ones).
// Sample covers all 14 type buckets; totals match UXNetworkHome's populated collections and
// default to 0 for definitions with no profiles yet created.
const SAMPLE_INTEL_PROFILES: IntelProfileSample[] = [
    // asset
    {key: "ASSET.BUILDING", name: "Buildings", icon: "place", type: "asset", total: 0},
    {key: "ASSET.CURRENCY", name: "Currencies", icon: "unit", type: "asset", total: 0},
    {key: "ASSET.LAND", name: "Land Parcels", icon: "place", type: "asset", total: 0},

    // attribute
    {key: "ATTRIBUTE.COLOR", name: "Colors", icon: "attribute", type: "attribute", total: 9},
    {key: "ATTRIBUTE.MATERIAL", name: "Materials", icon: "material", type: "attribute", total: 0},
    {key: "ATTRIBUTE.SIZE", name: "Sizes", icon: "attribute", type: "attribute", total: 0},
    {key: "ATTRIBUTE.STATUS", name: "Statuses", icon: "attribute", type: "attribute", total: 0},

    // category
    {key: "CATEGORY.CLASSIFICATION", name: "Classifications", icon: "metadata", type: "category", total: 0},
    {key: "CATEGORY.TAG_GROUP", name: "Tag Groups", icon: "collection", type: "category", total: 0},
    {key: "CATEGORY.TAXONOMY", name: "Taxonomies", icon: "collection", type: "category", total: 0},

    // contract
    {key: "CONTRACT.AGREEMENT", name: "Agreements", icon: "law", type: "contract", total: 0},
    {key: "CONTRACT.BUSINESS.BUSINESS_PARTNER", name: "Business Partners", icon: "credential", type: "contract", total: 2},
    {key: "CONTRACT.LICENSE", name: "Licenses", icon: "credential", type: "contract", total: 0},

    // entity
    {key: "ENTITY.ACCOUNT", name: "Accounts", icon: "account", type: "entity", total: 5},
    {key: "ENTITY.VEHICLE.AUTOMOBILE", name: "Automobiles", icon: "vehicle", type: "entity", total: 2},
    {key: "ENTITY.COMPANY", name: "Companies", icon: "company", type: "entity", total: 0},
    {key: "ENTITY.COMPUTER", name: "Computers", icon: "device", type: "entity", total: 23},
    {key: "ENTITY.DEVICE", name: "Devices", icon: "device", type: "entity", total: 0},
    {key: "ENTITY.ORGANIZATION", name: "Organizations", icon: "organization", type: "entity", total: 0},
    {key: "ENTITY.PERSON", name: "Persons", icon: "person", type: "entity", total: 0},
    {key: "ENTITY.PRODUCT", name: "Products", icon: "product", type: "entity", total: 0},

    // event
    {key: "EVENT.APPOINTMENT", name: "Appointments", icon: "event", type: "event", total: 60},
    {key: "EVENT.CONFERENCE", name: "Conferences", icon: "event", type: "event", total: 932},
    {key: "EVENT.INCIDENT", name: "Incidents", icon: "event", type: "event", total: 0},
    {key: "EVENT.MEETING", name: "Meetings", icon: "event", type: "event", total: 0},
    {key: "EVENT.TRANSACTION", name: "Transactions", icon: "event", type: "event", total: 0},

    // factor
    {key: "FACTOR.CONDITION", name: "Conditions", icon: "condition", type: "factor", total: 0},
    {key: "FACTOR.QUALITY", name: "Quality Factors", icon: "capability", type: "factor", total: 0},
    {key: "FACTOR.RISK", name: "Risk Factors", icon: "capability", type: "factor", total: 0},

    // identifier
    {key: "IDENTIFIER.EMAIL", name: "Email Addresses", icon: "email_address", type: "identifier", total: 0},
    {key: "IDENTIFIER.HOSTNAME", name: "Hostnames", icon: "hostname", type: "identifier", total: 0},
    {key: "IDENTIFIER.IP", name: "IP Addresses", icon: "ipaddress", type: "identifier", total: 0},
    {key: "IDENTIFIER.MAC", name: "MAC Addresses", icon: "mac_address", type: "identifier", total: 0},
    {key: "IDENTIFIER.PHONE", name: "Phone Numbers", icon: "phone_number", type: "identifier", total: 0},
    {key: "IDENTIFIER.URL", name: "URLs", icon: "url", type: "identifier", total: 0},
    {key: "IDENTIFIER.USERNAME", name: "Usernames", icon: "username", type: "identifier", total: 0},

    // measurement
    {key: "MEASUREMENT", name: "Binance Coin", icon: "unit", type: "measurement", total: 43},
    {key: "MEASUREMENT.COORDINATES", name: "Coordinates", icon: "coordinate", type: "measurement", total: 11},
    {key: "MEASUREMENT.DISTANCE", name: "Distances", icon: "unit", type: "measurement", total: 0},
    {key: "MEASUREMENT.TEMPERATURE", name: "Temperatures", icon: "unit", type: "measurement", total: 0},
    {key: "MEASUREMENT.WEIGHT", name: "Weights", icon: "unit", type: "measurement", total: 0},

    // place
    {key: "PLACE.ADDRESS", name: "Addresses", icon: "place", type: "place", total: 0},
    {key: "PLACE.CITY", name: "Cities", icon: "place", type: "place", total: 49},
    {key: "PLACE.COUNTRY", name: "Countries", icon: "place", type: "place", total: 0},
    {key: "PLACE.REGION", name: "Regions", icon: "place", type: "place", total: 0},

    // relationship
    {key: "RELATIONSHIP.PROFESSIONAL.ASSOCIATE", name: "Associates", icon: "relation", type: "relationship", total: 0},
    {key: "RELATIONSHIP.PERSONAL.CITIZEN", name: "Citizens", icon: "person", type: "relationship", total: 15},
    {key: "RELATIONSHIP.PROFESSIONAL.EMPLOYEE", name: "Employees", icon: "role", type: "relationship", total: 0},
    {key: "RELATIONSHIP.PERSONAL.FAMILY", name: "Family", icon: "person", type: "relationship", total: 3},

    // selector
    {key: "SELECTOR.EXPRESSION", name: "Expressions", icon: "selector", type: "selector", total: 0},
    {key: "SELECTOR.KEYWORD", name: "Keywords", icon: "selector", type: "selector", total: 0},
    {key: "SELECTOR.PATTERN", name: "Patterns", icon: "selector", type: "selector", total: 0},

    // temporal
    {key: "TEMPORAL.DATE", name: "Dates", icon: "metadata", type: "temporal", total: 0},
    {key: "TEMPORAL.DURATION", name: "Durations", icon: "metadata", type: "temporal", total: 0},
    {key: "TEMPORAL.PERIOD", name: "Time Periods", icon: "metadata", type: "temporal", total: 0},
];

function UXIntelCreateSelect() {
    const {networkUID} = useParams();
    const navigate = useNavigate();

    const [filterToggle, setFilterToggle] = useState<FilterToggle>("all");
    const [profiles, setProfiles] = useState<IntelProfileSample[]>([]);
    const [filterText, setFilterText] = useState("");

    useEffect(() => {
        // TODO: Replace with real API call to load intelligence definitions for this network
        setProfiles(SAMPLE_INTEL_PROFILES);
    }, []);

    const filtered = profiles.filter((p) => {
        const matchesToggle = filterToggle === "all" || p.type === filterToggle;
        const q = filterText.trim().toLowerCase();
        const matchesName = q.length === 0 || p.name.toLowerCase().includes(q);
        return matchesToggle && matchesName;
    });

    return (
        <Stack direction="column" width="100%" minHeight="100vh">
            {appSession.activeSession() === false ? (
                <Navigate to="/" />
            ) : (
                <Stack direction="column" spacing={1} width="99%">
                    <WidgetHeader
                        title="Create Profile"
                        breadcrumbs={[
                            {display: "Networks", linkTo: "/networks/", activeView: false},
                            {display: String(networkUID), linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Intelligence", linkTo: `/networks/${networkUID}`, activeView: false},
                            {display: "Create", linkTo: "", activeView: true},
                        ]}
                    />
                    <Stack
                        sx={appStyles.defaultPanelContainer}
                        spacing={2}
                        direction="column"
                        width="100%"
                        alignItems="center"
                    >
                        {/* Centered title */}
                        <Typography sx={{...appStyles.panelSectionTitle, padding: "16px 0 4px"}}>
                            Create Profile
                        </Typography>

                        {/* Filter + count */}
                        <Stack spacing={2} direction="column" width="100%" alignItems="center">
                            <Stack direction="row" alignItems="center" justifyContent="center">
                                <StyledSearch>
                                    <StyledSearchIconWrapper>
                                        <img src="/images/search_white.svg" width={16} />
                                    </StyledSearchIconWrapper>
                                    <StyledInputBase
                                        placeholder="Filter"
                                        value={filterText}
                                        onChange={(e) => setFilterText(e.target.value)}
                                        inputProps={{"aria-label": "filter"}}
                                    />
                                </StyledSearch>
                                <Typography sx={{...appStyles.showCountText, paddingTop: 1}}>
                                    Showing {filtered.length} of {profiles.length}
                                </Typography>
                            </Stack>
                        </Stack>

                        {/* Type toggle bar — mirrors UXNetworkHome type filter */}
                        <Stack spacing={0} padding={1} direction="column" width="100%" alignItems="center">
                            <ToggleButtonGroup
                                value={filterToggle}
                                exclusive
                                aria-label="filter"
                                onChange={(_, newValue: FilterToggle | null) => {
                                    if (newValue) setFilterToggle(newValue);
                                }}
                                sx={appStyles.toggleButtonStyle}
                            >
                                <StyledFilterToggleButton value="all" aria-label="all">All</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="asset" aria-label="asset">Assets</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="attribute" aria-label="attribute">Attributes</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="category" aria-label="category">Categories</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="contract" aria-label="contract">Contracts</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="entity" aria-label="entity">Entities</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="event" aria-label="event">Events</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="factor" aria-label="factor">Factors</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="identifier" aria-label="identifier">Identifiers</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="measurement" aria-label="measurement">Measurements</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="place" aria-label="place">Places</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="relationship" aria-label="relationship">Relationships</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="selector" aria-label="selector">Selectors</StyledFilterToggleButton>
                                <StyledFilterToggleButton value="temporal" aria-label="temporal">Temporals</StyledFilterToggleButton>
                            </ToggleButtonGroup>
                        </Stack>

                        {/* Card grid — same 3-column layout as UXNetworkHome intel list */}
                        <Stack direction="column" alignItems="center" width="100%">
                            <Box sx={appStyles.defaultPanelCardsWrapper} width="98%" height="100%">
                                <Box sx={{overflowY: "auto", flexGrow: 1, p: 1}}>
                                    <Box
                                        sx={{
                                            display: "grid",
                                            gridTemplateColumns: "repeat(3, 330px)",
                                            gap: 2,
                                            justifyContent: "center",
                                            justifyItems: "center",
                                            width: "100%",
                                        }}
                                    >
                                        {profiles.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                Loading profile data...
                                            </Typography>
                                        ) : filtered.length === 0 ? (
                                            <Typography sx={{color: "#a8b8c0", fontSize: "0.85rem", padding: "32px 0"}}>
                                                No intelligence profiles match the current filter.
                                            </Typography>
                                        ) : (
                                            filtered.map((nextProfile) => (
                                                <IntelProfileCard
                                                    key={`create_${nextProfile.key}`}
                                                    entry={nextProfile}
                                                    networkUID={String(networkUID)}
                                                    onSelect={() => navigate(
                                                        `/networks/${networkUID}/intel/${nextProfile.type}/${nextProfile.key}/create`,
                                                        { state: { profile: nextProfile } }
                                                    )}
                                                />
                                            ))
                                        )}
                                    </Box>
                                </Box>
                            </Box>
                        </Stack>
                    </Stack>
                </Stack>
            )}
        </Stack>
    );
}

export default UXIntelCreateSelect;
